import { Autocomplete, type Item } from '@ads/components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRevisionClassStudentDropdown } from '@/features/exam-revision/hooks/useRevisionClassStudentDropdown';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';

export interface ClassStudentAutocompleteProps {
  /** ID da turma selecionada; `''` quando nenhuma. */
  readonly value: string;
  /** Notifica o novo ID de turma selecionado (`''` ao limpar). */
  readonly onChange: (classStudentId: string) => void;
  readonly label: string;
  readonly placeholder: string;
}

/**
 * Autocomplete de turmas (ClassStudent) com busca server-side (debounce + mínimo
 * de 3 caracteres), no mesmo padrão dos autocompletes da tela de pessoas.
 *
 * Mantém internamente o termo pesquisado e o rótulo selecionado; para fora expõe
 * apenas `value` (ID) e `onChange`, mantendo a tela como dona da seleção.
 */
export function ClassStudentAutocomplete({
  value,
  onChange,
  label,
  placeholder,
}: ClassStudentAutocompleteProps) {
  const { t: tCommon } = useTranslation('common');

  const [search, setSearch] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');

  const { classes, isQuerySuccess } = useRevisionClassStudentDropdown(search);

  const items = classes.map((c): Item<string> => ({ label: c.name, value: c.id }));
  const selectedItem: Item<string> = value
    ? { label: selectedLabel, value }
    : { label: '', value: '' };

  const notFoundMessage = getAutocompleteSearchMessage(
    search.length,
    isQuerySuccess,
    items.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  return (
    <Autocomplete<string>
      label={label}
      placeholder={placeholder}
      size="md"
      list={items}
      custom={{ input: '!w-full' }}
      selectedItem={selectedItem}
      {...(notFoundMessage !== undefined ? { notFoundMessage } : {})}
      onChange={e => setSearch(e.target.value)}
      handleChange={item => {
        onChange(item.value ?? '');
        setSelectedLabel(item.label ?? '');
        setSearch('');
      }}
      handleClearQuery={() => {
        onChange('');
        setSelectedLabel('');
        setSearch('');
      }}
    />
  );
}
